import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText, Scale, AlertTriangle, Loader2, AlertCircle } from 'lucide-react';
import Navbar from '../components/common/Navbar';
import Footer from '../components/common/Footer';
import { usePageMetadata } from '../hooks/usePageMetadata';
import { API_BASE_URL } from '../config/api';

interface TermsData {
    title: string;
    effectiveDate: string;
    content: string;
}

const TermsOfService = () => {
    // Set page metadata
    usePageMetadata('terms');

    const [termsData, setTermsData] = useState<TermsData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        fetchTermsData();
    }, []);

    const fetchTermsData = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${API_BASE_URL}/api/get-terms-and-condition`, {
                method: 'GET',
                headers: {
                    'Authorization': 'Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1c2VySWQiOiI3IiwiZW1haWwiOiJhbWl0Y2FycGVudGVyMTk4QGdtYWlsLmNvbSIsImlhdCI6MTc1Nzk0NDA5NSwiZXhwIjoxNzYwNjIyNDk1fQ.H9I-Mm2rkhg4Da4J_65HG918Wt2TmZNTAmWDg2EpQrA',
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                throw new Error('Failed to fetch terms data');
            }

            const htmlContent = await response.text();

            // Parse the HTML content to extract structured data
            const parser = new DOMParser();
            const doc = parser.parseFromString(htmlContent, 'text/html');

            // Extract title and effective date
            const title = doc.querySelector('h1')?.textContent || 'Terms and Conditions';
            const effectiveDateElement = doc.querySelector('.effective-date');
            const effectiveDate = effectiveDateElement?.textContent?.replace('Effective Date:', '').trim() || 'January 1, 2025';

            setTermsData({
                title,
                effectiveDate,
                content: htmlContent
            });
        } catch (err) {
            setError('Failed to load terms and conditions. Please try again later.');
            console.error('Error fetching terms data:', err);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-white">
                <Navbar />
                <div className="flex items-center justify-center min-h-[60vh] px-4">
                    <div className="text-center">
                        <Loader2 className="w-6 h-6 sm:w-8 sm:h-8 animate-spin text-gray-600 mx-auto mb-3 sm:mb-4" />
                        <p className="text-sm sm:text-base text-gray-600">Loading terms and conditions...</p>
                    </div>
                </div>
                <Footer />
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-white">
                <Navbar />
                <div className="flex items-center justify-center min-h-[60vh] px-4">
                    <div className="text-center">
                        <AlertCircle className="w-10 h-10 sm:w-12 sm:h-12 text-red-500 mx-auto mb-3 sm:mb-4" />
                        <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">Error Loading Content</h2>
                        <p className="text-sm sm:text-base text-gray-600 mb-4">{error}</p>
                        <button
                            onClick={fetchTermsData}
                            className="px-4 py-2 text-sm sm:text-base bg-gray-800 text-white rounded-lg hover:bg-gray-900 transition-colors"
                        >
                            Try Again
                        </button>
                    </div>
                </div>
                <Footer />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-white">
            <Navbar />

            {/* Simple Header */}
            <div className="bg-white border-b border-gray-200 py-8 sm:py-12">
                <div className="container mx-auto px-4 sm:px-6">
                    <div className="max-w-4xl mx-auto">
                        <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
                            <Link
                                to="/"
                                className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors duration-200"
                            >
                                <ArrowLeft className="w-4 h-4" />
                                <span className="text-xs sm:text-sm font-medium">Back to Home</span>
                            </Link>
                        </div>

                        <div>
                            <h1 className="text-2xl sm:text-3xl font-bold mb-2 text-gray-900">{termsData?.title || 'Terms and Conditions'}</h1>
                            <p className="text-gray-600 text-xs sm:text-sm mb-3 sm:mb-4">Diasporawise Investment Platform</p>
                            <div className="bg-gray-50 rounded-lg px-3 sm:px-4 py-2 inline-block border border-gray-200">
                                <span className="text-xs sm:text-sm font-medium text-gray-700">
                                    <strong>Effective Date:</strong> {termsData?.effectiveDate || 'January 1, 2025'}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="py-8 sm:py-12 md:py-16">
                <div className="container mx-auto px-4 sm:px-6">
                    <div className="max-w-4xl mx-auto">
                        <div className="prose prose-sm sm:prose-base md:prose-lg max-w-none">
                            {/* Content Body */}
                            <div
                                className="terms-content text-gray-700 leading-relaxed"
                                dangerouslySetInnerHTML={{
                                    __html: termsData?.content
                                        ? termsData.content
                                            .replace(/<p[^>]*>.*?Phone.*?<\/p>/gi, '')
                                            .replace(/<p[^>]*>.*?\+1.*?<\/p>/gi, '')
                                            .replace(/<p[^>]*>.*?\+234.*?<\/p>/gi, '')
                                            .replace(/<p[^>]*>.*?\(555\).*?<\/p>/gi, '')
                                            .replace(/<p[^>]*>.*?\(234\).*?<\/p>/gi, '')
                                            .replace(/\+1 \(234\) 567-890/gi, '')
                                            .replace(/123 Investment Plaza.*?India/gi, '123 Investment St, Finance City, FC 12345')
                                            .replace(/Investment Plaza.*?Maharashtra/gi, '123 Investment St, Finance City, FC 12345')
                                            .replace(/support@diasporawise\.com/gi, 'info@diasporawise.com')
                                            .replace(/privacy@diasporawise\.com/gi, 'info@diasporawise.com')
                                            .replace(/admin@diasporawise\.com/gi, 'info@diasporawise.com')
                                            .replace(/contact@diasporawise\.com/gi, 'info@diasporawise.com')
                                            .replace(/support@realestateinvestment.com/gi, 'info@diasporawise.com')
                                            .replace(/<p[^>]*>.*?Phone:.*?<\/p>/gi, '')
                                            .replace(/Phone:.*?<br\s*\/?>/gi, '')
                                            .replace(/Phone:.*?$/gm, '')
                                        : ''
                                }}
                            />

                        </div>
                    </div>
                </div>
            </div>

            <Footer />
        </div >
    );
};

export default TermsOfService;